// Mock data for Drishti Nimawat Designs

export const navLinks = [
  { name: 'Home', href: '#home' },
  { name: 'About', href: '#about' },
  { name: 'Services', href: '#services' },
  { name: 'Blog', href: '#blog' },
  { name: 'Testimonials', href: '#testimonials' },
  { name: 'Contact', href: '#contact' }
];

export const heroData = {
  title: 'Designed For Life Styled for Luxury',
  subtitle: 'Crafting Timeless Spaces with Elegance',
  description: 'Where luxury meets functionality in every design',
  cta: 'Book Appointment'
};

export const aboutData = {
  title: 'About Us',
  description: 'Drishti Nimawat Designs is a premier luxury interior and architectural design firm dedicated to creating spaces that transcend the ordinary. With a commitment to excellence and an eye for detail, we transform visions into stunning realities.',
  mission: 'Our mission is to craft environments that not only reflect our clients\' unique personalities but also enhance their quality of life through thoughtful, sophisticated design.',
  values: [
    { title: 'Excellence', description: 'Uncompromising quality in every project' },
    { title: 'Innovation', description: 'Pushing boundaries with creative solutions' },
    { title: 'Integrity', description: 'Transparent and honest relationships' },
    { title: 'Timelessness', description: 'Designs that endure beyond trends' }
  ]
};

export const servicesData = [
  {
    id: 1,
    title: 'Interior Design',
    description: 'Bespoke interior solutions that blend aesthetics with functionality, creating spaces that inspire and delight.',
    image: '/images/image1.webp',
    features: ['Space Planning', 'Custom Furniture', 'Upholestry', 'Lighting Design']
  },
  {
    id: 2,
    title: 'Architectural Design',
    description: 'Innovative architectural solutions that harmonize form and function, creating structures that stand the test of time.',
    image: '/images/image2.webp',
    features: ['Concept And Design Development', 'Technical Drawings', 'Landscaping', 'Project Management']
  },
  {
    id: 3,
    title: '3D Visualization',
    description: 'Photorealistic renderings that bring your vision to life before the first brick is laid.',
    image: '/images/image3.webp',
    features: ['3D Modeling', 'Photorealistic Renders', 'Virtual Tours', 'Animation']
  },
  {
    id: 4,
    title: 'Design Consultation',
    description: 'Expert guidance and strategic planning to ensure your project exceeds expectations.',
    image: '/images/image4.webp',
    features: ['Design Development', 'Budget Planning', 'Material Selection And Sourcing', 'Vendor Coordination']
  }
];

export const blogsData = [
  {
    id: 1,
    title: 'The Art of Minimalist Luxury',
    excerpt: 'Discover how less can truly be more when it comes to creating sophisticated, luxurious spaces that breathe elegance.',
    image: '/images/image5.webp',
    date: 'December 10, 2024',
    category: 'Design Trends',
    readTime: '5 min read',
    content: `
      <h2>The Philosophy of Minimalist Luxury</h2>
      <p>In the world of interior design, minimalist luxury represents a refined approach that celebrates quality over quantity. This design philosophy embraces the principle that true luxury lies not in excess, but in the careful curation of exceptional elements.</p>
      
      <h3>Key Principles</h3>
      <p>Minimalist luxury is built on several foundational principles: intentional space planning, premium materials, and thoughtful restraint. Each piece in a minimalist luxury space serves a purpose, whether functional or aesthetic, and is chosen with meticulous care.</p>
      
      <h3>Material Selection</h3>
      <p>The materials used in minimalist luxury design speak volumes. Natural stone, fine woods, premium metals, and luxurious textiles create a tactile experience that defines sophistication. These materials age beautifully, adding character and depth over time.</p>
      
      <h3>Creating Harmony</h3>
      <p>The true art lies in creating harmony through simplicity. Neutral color palettes, clean lines, and uncluttered spaces allow the architecture and carefully selected pieces to shine. Lighting plays a crucial role, with both natural and artificial light sources designed to enhance the space's inherent beauty.</p>
    `
  },
  {
    id: 2,
    title: 'Sustainable Design: The Future of Architecture',
    excerpt: 'Exploring eco-friendly practices and sustainable materials that are shaping the future of modern architecture.',
    image: '/images/image6.webp',
    date: 'December 5, 2024',
    category: 'Sustainability',
    readTime: '7 min read',
    content: `
      <h2>Building for Tomorrow, Today</h2>
      <p>Sustainable architecture is no longer a trend—it's a necessity. As we face environmental challenges, the architecture and design community has a responsibility to create buildings that minimize environmental impact while maximizing efficiency and beauty.</p>
      
      <h3>Eco-Friendly Materials</h3>
      <p>Modern sustainable design leverages innovative materials: reclaimed wood, recycled metal, bamboo, and low-VOC finishes. These materials reduce environmental footprint without compromising on aesthetics or durability.</p>
      
      <h3>Energy Efficiency</h3>
      <p>Smart design integrates passive heating and cooling, solar orientation, and high-performance insulation. Triple-glazed windows, green roofs, and solar panels transform buildings into energy-positive structures.</p>
      
      <h3>Water Conservation</h3>
      <p>Rainwater harvesting systems, greywater recycling, and drought-resistant landscaping are essential components of sustainable design. These systems reduce water consumption while maintaining beautiful, functional spaces.</p>
    `
  },
  {
    id: 3,
    title: 'Color Psychology in Interior Spaces',
    excerpt: 'Understanding how color influences mood, behavior, and the overall atmosphere of your living spaces.',
    image: '/images/image7.webp',
    date: 'November 28, 2024',
    category: 'Design Tips',
    readTime: '6 min read',
    content: `
      <h2>The Power of Color</h2>
      <p>Color is one of the most powerful tools in a designer's arsenal. It influences our emotions, affects our perception of space, and can even impact our physiological responses. Understanding color psychology is essential for creating spaces that truly resonate.</p>
      
      <h3>Warm vs. Cool Tones</h3>
      <p>Warm colors (reds, oranges, yellows) energize and stimulate, making them ideal for social spaces. Cool colors (blues, greens, purples) calm and soothe, perfect for bedrooms and relaxation areas.</p>
      
      <h3>Neutral Sophistication</h3>
      <p>Neutral palettes—whites, grays, beiges, and taupes—create timeless elegance. They serve as the perfect backdrop for accent colors and allow architectural features and furnishings to take center stage.</p>
      
      <h3>Creating Balance</h3>
      <p>The key to successful color use is balance. The 60-30-10 rule suggests 60% dominant color, 30% secondary color, and 10% accent color. This creates visual harmony while maintaining interest.</p>
    `
  },
  {
    id: 4,
    title: 'Maximizing Small Spaces: Design Strategies',
    excerpt: 'Innovative techniques to make compact spaces feel open, airy, and incredibly functional without sacrificing style.',
    image: '/images/image8.webp',
    date: 'November 20, 2024',
    category: 'Space Planning',
    readTime: '5 min read',
    content: `
      <h2>Small Space, Big Impact</h2>
      <p>Limited square footage doesn't mean limited possibilities. With strategic design choices, small spaces can feel expansive, functional, and beautifully styled. The key is thoughtful planning and creative solutions.</p>
      
      <h3>Multi-Functional Furniture</h3>
      <p>Invest in pieces that serve multiple purposes: sofa beds, expandable dining tables, storage ottomans, and wall-mounted desks. These pieces maximize functionality without cluttering the space.</p>
      
      <h3>Vertical Thinking</h3>
      <p>Use vertical space wisely with tall bookcases, wall-mounted storage, and floating shelves. Drawing the eye upward creates the illusion of height and provides valuable storage.</p>
      
      <h3>Light and Mirrors</h3>
      <p>Natural light and strategically placed mirrors expand visual space dramatically. Large windows, reflective surfaces, and light color palettes create an open, airy atmosphere.</p>
      
      <h3>Declutter and Organize</h3>
      <p>In small spaces, organization is paramount. Built-in storage, hidden compartments, and regular decluttering keep spaces feeling open and serene.</p>
    `
  },
  {
    id: 5,
    title: 'The Renaissance of Art Deco Design',
    excerpt: 'How this iconic 1920s design movement is making a stunning comeback in contemporary interiors.',
    image: '/images/image9.webp',
    date: 'November 12, 2024',
    category: 'Design History',
    readTime: '6 min read',
    content: `
      <h2>Glamour Returns</h2>
      <p>Art Deco, with its bold geometric patterns, rich colors, and luxurious materials, is experiencing a remarkable revival. Modern designers are reinterpreting this glamorous style for contemporary living.</p>
      
      <h3>Signature Elements</h3>
      <p>Art Deco is characterized by geometric shapes, symmetrical designs, rich jewel tones, and luxurious materials like marble, brass, and velvet. These elements create drama and sophistication.</p>
      
      <h3>Modern Interpretation</h3>
      <p>Today's Art Deco revival balances period authenticity with modern sensibility. Clean lines meet ornate details, creating spaces that feel both timeless and contemporary.</p>
      
      <h3>Color and Pattern</h3>
      <p>Deep emeralds, sapphire blues, and ruby reds pair with gold and black accents. Geometric patterns in wallpaper, textiles, and flooring add visual interest and movement.</p>
      
      <h3>Lighting as Jewelry</h3>
      <p>Statement lighting fixtures—chandeliers, sconces, and table lamps—serve as jewelry for the room, adding sparkle and glamour reminiscent of the Roaring Twenties.</p>
    `
  },
  {
    id: 6,
    title: 'Biophilic Design: Bringing Nature Indoors',
    excerpt: 'Discover how integrating natural elements into interior design enhances wellbeing and creates harmonious living spaces.',
    image: '/images/image10.webp',
    date: 'November 5, 2024',
    category: 'Wellness',
    readTime: '7 min read',
    content: `
      <h2>The Nature Connection</h2>
      <p>Biophilic design recognizes humans' innate connection to nature and incorporates natural elements into built environments. This approach has been shown to reduce stress, enhance creativity, and improve overall wellbeing.</p>
      
      <h3>Natural Materials</h3>
      <p>Wood, stone, bamboo, and natural fibers create tactile connections to nature. These materials bring warmth, texture, and authenticity to interiors while supporting sustainability.</p>
      
      <h3>Living Elements</h3>
      <p>Indoor plants purify air and create living focal points. From statement fiddle-leaf figs to trailing pothos, plants add life, color, and movement to spaces.</p>
      
      <h3>Natural Light</h3>
      <p>Maximizing natural light through large windows, skylights, and light wells connects occupants to daily and seasonal rhythms, supporting circadian health.</p>
      
      <h3>Water Features</h3>
      <p>The sound and sight of water—fountains, aquariums, or reflective pools—create calming focal points that engage multiple senses.</p>
      
      <h3>Organic Shapes</h3>
      <p>Curved lines, natural forms, and organic patterns evoke nature's fluidity, creating spaces that feel more relaxed and welcoming than rigid geometric designs.</p>
    `
  }
];

export const testimonialsData = [
  {
    id: 1,
    name: 'Sanjay Mathur',
    role: 'Homeowner',
    content: 'Drishti Nimawat Designs transformed our house into a home that truly reflects who we are. Their attention to detail and commitment to excellence exceeded all our expectations.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop'
  },
  {
    id: 2,
    name: 'Vipin Yadav',
    role: 'CEO, Tech Innovations',
    content: 'Working with this team was an absolute pleasure. They delivered a stunning office space that has become a source of pride for our entire company. Professional, creative, and reliable.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop'
  },
  {
    id: 3,
    name: '⁠Shantanu Singh',
    role: 'Restaurant Owner',
    content: 'The design of our restaurant has become a major draw for customers. The ambiance they created is exactly what we envisioned—elegant, welcoming, and unforgettable.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop'
  },
  {
    id: 4,
    name: '⁠Tanuja Gautam',
    role: 'Property Developer',
    content: 'Their innovative approach to architectural design helped us create properties that stand out in the market. Exceptional creativity combined with practical execution.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'
  }
];

export const contactData = {
  title: 'Let\'s Create Something Beautiful',
  description: 'Ready to transform your space? Get in touch with us to discuss your project.',
  email: 'drishtinimawatdesigns@gmail.com',
  phone: '+91 70736 20688',
  address: '2nd Floor, A-16, Patrakar Colony Rd, Sheer Sagar Patarkar Colony, Mansarovar, Jaipur, Rajasthan 302020',
  socials: [
    { name: 'Facebook', url: '#', icon: 'facebook' },
    { name: 'LinkedIn', url: '#', icon: 'linkedin' },
    { name: 'Instagram', url: 'https://www.instagram.com/drishtinimawatdesigns/', icon: 'instagram' }
  ]
};
